import React, { useEffect, useRef, useState, useCallback } from "react";
import { Bell, CheckCheck } from "lucide-react";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Button } from "@/components/ui/button";
import { ScrollArea } from "@/components/ui/scroll-area";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { toast } from "sonner";

const SEV_STYLES = {
  danger:  { dot: "bg-red-400",     text: "text-red-300" },
  warning: { dot: "bg-amber-400",   text: "text-amber-300" },
  success: { dot: "bg-emerald-400", text: "text-emerald-300" },
  info:    { dot: "bg-sky-400",     text: "text-sky-300" },
};

function fmtTime(iso) {
  try {
    const d = new Date(iso);
    const diff = Math.floor((Date.now() - d.getTime()) / 1000);
    if (diff < 60) return "just now";
    if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
    if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
    return d.toLocaleDateString();
  } catch { return ""; }
}

export default function NotificationBell() {
  const [alerts, setAlerts] = useState([]);
  const [unread, setUnread] = useState(0);
  const [open, setOpen] = useState(false);
  const lastSeenIds = useRef(new Set());
  const permAsked = useRef(false);
  const nav = useNavigate();

  const requestPerm = useCallback(() => {
    if (permAsked.current) return;
    permAsked.current = true;
    if (typeof Notification !== "undefined" && Notification.permission === "default") {
      Notification.requestPermission().catch(() => {});
    }
  }, []);

  const fireBrowserNotif = useCallback((a) => {
    try {
      if (typeof Notification !== "undefined" && Notification.permission === "granted") {
        new Notification("BetTrackr", { body: a.message, tag: a.alert_id, icon: "/bettrackr-logo.svg" });
      }
    } catch {}
  }, []);

  const load = useCallback(async (silent = false) => {
    try {
      // Refresh live scores server-side first, which may create alerts
      await api.post("/slips/refresh-all").catch(() => {});
      const { data } = await api.get("/alerts", { params: { limit: 30 } });
      setAlerts(data.alerts);
      setUnread(data.unread_count);
      // Detect new alerts (not seen in this session) and fire toasts + browser notif
      if (!silent) {
        const fresh = data.alerts.filter((a) => !a.read && !lastSeenIds.current.has(a.alert_id));
        fresh.forEach((a) => {
          const sev = SEV_STYLES[a.severity] || SEV_STYLES.info;
          if (a.severity === "danger" || a.severity === "warning") toast.error(a.message);
          else if (a.severity === "success") toast.success(a.message);
          else toast(a.message);
          fireBrowserNotif(a);
        });
      }
      data.alerts.forEach((a) => lastSeenIds.current.add(a.alert_id));
    } catch {}
  }, [fireBrowserNotif]);

  useEffect(() => {
    requestPerm();
    // Initial silent load so we don't spam toasts for existing alerts on first mount
    (async () => {
      try {
        const { data } = await api.get("/alerts", { params: { limit: 30 } });
        setAlerts(data.alerts);
        setUnread(data.unread_count);
        data.alerts.forEach((a) => lastSeenIds.current.add(a.alert_id));
      } catch {}
    })();
    const t = setInterval(() => load(false), 30000);
    return () => clearInterval(t);
  }, [load, requestPerm]);

  const markAllRead = async () => {
    await api.post("/alerts/read-all");
    setAlerts((xs) => xs.map((a) => ({ ...a, read: true })));
    setUnread(0);
  };

  const openAlert = async (a) => {
    if (!a.read) {
      await api.post(`/alerts/${a.alert_id}/read`);
      setAlerts((xs) => xs.map((x) => x.alert_id === a.alert_id ? { ...x, read: true } : x));
      setUnread((n) => Math.max(0, n - 1));
    }
    setOpen(false);
    if (a.slip_id) nav(`/slip/${a.slip_id}`);
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          data-testid="notification-bell"
          className="relative h-9 w-9 rounded-md hover:bg-white/5 flex items-center justify-center text-slate-300 hover:text-white transition-colors"
        >
          <Bell className="h-5 w-5" />
          {unread > 0 && (
            <span
              data-testid="notification-unread-count"
              className="absolute -top-1 -right-1 h-4 min-w-[16px] px-1 rounded-full bg-red-500 text-white text-[10px] font-bold flex items-center justify-center"
            >
              {unread > 9 ? "9+" : unread}
            </span>
          )}
        </button>
      </PopoverTrigger>
      <PopoverContent
        align="end"
        data-testid="notification-popover"
        className="w-80 sm:w-96 p-0 bg-[#121824] border-white/10 text-white"
      >
        <div className="flex items-center justify-between px-4 py-3 border-b border-white/10">
          <div>
            <div className="text-xs uppercase tracking-widest text-emerald-400">Live Alerts</div>
            <div className="text-sm font-semibold">{unread} unread</div>
          </div>
          <Button
            variant="ghost"
            size="sm"
            data-testid="mark-all-read-button"
            onClick={markAllRead}
            disabled={unread === 0}
            className="text-slate-300 hover:text-white text-xs"
          >
            <CheckCheck className="h-3 w-3 mr-1" /> Mark all read
          </Button>
        </div>
        <ScrollArea className="max-h-96">
          {alerts.length === 0 ? (
            <div className="px-4 py-10 text-center text-sm text-slate-400">
              No alerts yet. We'll ping you the moment a leg turns.
            </div>
          ) : (
            <ul>
              {alerts.map((a) => {
                const s = SEV_STYLES[a.severity] || SEV_STYLES.info;
                return (
                  <li key={a.alert_id}>
                    <button
                      data-testid={`alert-item-${a.alert_id}`}
                      onClick={() => openAlert(a)}
                      className={`w-full text-left px-4 py-3 flex gap-3 items-start border-b border-white/5 hover:bg-white/5 transition-colors ${!a.read ? "bg-emerald-500/[0.03]" : ""}`}
                    >
                      <span className={`mt-1.5 h-2 w-2 rounded-full flex-shrink-0 ${s.dot} ${!a.read ? "animate-pulse" : ""}`} />
                      <div className="flex-1 min-w-0">
                        <div className={`text-sm ${!a.read ? "text-white font-semibold" : "text-slate-300"}`}>{a.message}</div>
                        <div className="text-[10px] uppercase tracking-widest text-slate-500 mt-1">{fmtTime(a.created_at)}</div>
                      </div>
                    </button>
                  </li>
                );
              })}
            </ul>
          )}
        </ScrollArea>
      </PopoverContent>
    </Popover>
  );
}
